import React from "react";
import styled from "styled-components";

const EventDetail = ({ startTime, endTime, isCrossDate, files, prMeta, prLink, googleDocs, figmaFiles, discordDraft, onDiscordClick }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const discordCount = discordDraft?.messages?.length ?? 0;

    return (
        <DetailWrap>
            <CalendarDetail startTime={startTime} endTime={endTime} isCrossDate={isCrossDate} />
            <FileDetails files={files} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
            {prMeta && <PRSummary prLink={prLink} prMeta={prMeta} />}
            {googleDocs && Array.isArray(googleDocs) && (
                <GoogleDocSummary googleDocs={googleDocs} />
            )}
            {figmaFiles && Array.isArray(figmaFiles) && (
                <FigmaSummary figmaFiles={figmaFiles} />
            )}
            {discordCount > 0 && (
                <DiscordSummary discordDraft={discordDraft} onDiscordClick={onDiscordClick} />
            )}
        </DetailWrap>
    );
};

const CalendarDetail = ({ startTime, endTime, isCrossDate }) => (
    <CalendarDetailWrapper>
        <EventDetailItem>
            <CalendarIcon src="/images/calendar.png" alt="Date" />
            <TimeWrapper>
                {startTime} - <EndTime $isCrossDate={isCrossDate}>{endTime}</EndTime>
            </TimeWrapper>
        </EventDetailItem>
    </CalendarDetailWrapper>
);

const FileDetails = ({ files, isExpanded, setIsExpanded }) => {
    const visibleFiles = isExpanded ? files : files.slice(0, 3);
    const timerRef = React.useRef(null);

    const handleMouseEnter = () => {
        if (!isExpanded) {
            timerRef.current = setTimeout(() => {
                setIsExpanded(true);
            }, 200); // 200ms delay
        }
    };

    const handleMouseLeave = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    React.useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return (
        <>
            {visibleFiles.map((file, index) => (
                <PRDetailItem key={index}>
                    <PRIcon src="/images/file_doc.svg" alt="File" />
                    <PRTextWrapper>
                        <PRMeta title={file.name}>
                            {file.url ? (
                                <PRLink
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {file.name}
                                </PRLink>
                            ) : (
                                file.name
                            )}
                        </PRMeta>
                    </PRTextWrapper>
                </PRDetailItem>
            ))}
            {files.length > 3 && !isExpanded && (
                <PRDetailItem>
                    <ExpandFormat onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        {`${files.length - 3} more file${files.length - 3 > 1 ? "s" : ""}...`}
                    </ExpandFormat>
                </PRDetailItem>
            )}
            {isExpanded && (
                <PRDetailItem>
                    <ExpandFormat onClick={() => setIsExpanded(false)}>Show less</ExpandFormat>
                </PRDetailItem>
            )}
        </>
    );
};

const PRSummary = ({ prLink, prMeta }) => {
    const { additions = 0, deletions = 0, changedFiles: changed_files = 0, commitCount: commits = 0 } = prMeta;

    return (
        <PRDetailItem>
            <PRIcon src="/images/github.svg" alt="PR" />
            <PRTextWrapper>
                <PRMeta>
                    {changed_files} file{changed_files !== 1 ? "s" : ""} ·{" "}
                    <AdditionText>+{additions}</AdditionText> / <DeletionText>-{deletions}</DeletionText> ·{" "}
                    <PRLink href={prLink} target="_blank" rel="noopener noreferrer">
                        {commits} commit{commits !== 1 ? "s" : ""}
                    </PRLink>
                </PRMeta>
            </PRTextWrapper>
        </PRDetailItem>
    );
};


const GoogleDocSummary = ({ googleDocs }) => {
    if (!Array.isArray(googleDocs)) return null;

    // Filter out invalid or empty docs
    const withMeta = googleDocs.filter(d => d?.meta && typeof d.meta === "object");
    const withoutMeta = googleDocs.filter(d => !d?.meta && d?.link);

    if (withMeta.length === 0 && withoutMeta.length === 0) return null;


    return (
        <>
            {withMeta.map((doc, idx) => {
                // Destructure or assign variables from doc as needed
                return (
                    <PRDetailItem key={idx}>
                        <PRIcon src="/images/google-doc.png" alt="Google Doc" />
                        <PRTextWrapper>
                            {/* Word count with link */}
                            <PRMeta>
                                {doc.meta.wordChange ? (
                                    doc.meta.wordChange.added === 0 && doc.meta.wordChange.removed === 0 ? (
                                        <>
                                            <p>Same Doc</p>
                                        </>
                                    ) : (
                                        <>
                                            <AdditionText>+{doc.meta.wordChange.added}</AdditionText> / <DeletionText>-{doc.meta.wordChange.removed}</DeletionText>
                                        </>
                                    )
                                ) : (
                                    <>
                                        <p>Version 1</p>
                                    </>
                                )} ·{" "}
                                <PRLink href={doc.meta.pdfStorage?.url || doc.link} target="_blank" rel="noopener noreferrer">
                                    {doc.meta.wordCount} word{doc.meta.wordCount !== 1 ? "s" : ""}
                                </PRLink>
                            </PRMeta>
                        </PRTextWrapper>
                    </PRDetailItem>
                );
            })}

            {/* Render docs without meta (missing or failed fetch) */}
            {withoutMeta.map((doc, idx) => (
                <PRDetailItem key={`missing-${idx}`}>
                    <PRIcon src="/images/google-doc.png" alt="Missing Doc" />
                    <PRTextWrapper>
                        <MissingMeta>
                            ❌ Metadata not fetched:
                            <PRLink href={doc.link} target="_blank" rel="noopener noreferrer">
                                {doc.name || "Doc Link"}
                            </PRLink>
                        </MissingMeta>
                    </PRTextWrapper>
                </PRDetailItem>
            ))}
        </>
    );
};

const FigmaSummary = ({ figmaFiles }) => {
    if (!Array.isArray(figmaFiles)) return null;

    const withMeta = figmaFiles.filter(
        f => f?.meta && typeof f.meta === "object" && Object.keys(f.meta).length > 0
    );
    const withoutMeta = figmaFiles.filter(
        f => (!f?.meta || Object.keys(f.meta).length === 0) && f?.link
    );

    if (withMeta.length === 0 && withoutMeta.length === 0) return null;

    return (
        <>
            {withMeta.map((fig, idx) => {
                const meta = fig.meta;
                const count = meta.versionChange?.count ?? 0;
                const status = meta.versionChange?.status || "—";
                const type = meta.fileType
                    ? meta.fileType.charAt(0).toUpperCase() + meta.fileType.slice(1)
                    : "Unknown";

                return (
                    <PRDetailItem key={idx}>
                        <PRIcon src="/images/figma.png" alt="Figma" />
                        <PRTextWrapper>
                            <PRMeta>
                                <PRLink
                                    href={meta.pdfStorage?.url || fig.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {count} edit{count !== 1 ? "s" : ""}
                                </PRLink>{" "}
                                ·{" "}
                                {status === "initial"
                                    ? "Version 1"
                                    : status === "no_change"
                                        ? "Same"
                                        : status === "updated"
                                            ? "New version"
                                            : status}{" "}
                                · {type}
                            </PRMeta>
                        </PRTextWrapper>
                    </PRDetailItem>
                );
            })}

            {withoutMeta.map((fig, idx) => (
                <PRDetailItem key={`missing-${idx}`}>
                    <PRIcon src="/images/figma.png" alt="Missing Figma" />
                    <PRTextWrapper>
                        <MissingMeta>
                            ❌ Metadata not fetched:
                            <PRLink
                                href={fig.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {fig.name || "Figma Link"}
                            </PRLink>
                        </MissingMeta>
                    </PRTextWrapper>
                </PRDetailItem>
            ))}
        </>
    );
};

const DiscordSummary = ({ discordDraft, onDiscordClick }) => {
    const discordCount = discordDraft?.messages?.length ?? 0;
    const handleClick = () => {
        if (onDiscordClick && discordDraft) onDiscordClick(discordDraft);
    };
    const handleKeyDown = (e) => {
        if (onDiscordClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleClick();
        }
    };
    const messageText = `${discordCount} message${discordCount !== 1 ? "s" : ""}`;
    return (
        <DiscordSummaryRow
            role={onDiscordClick ? "button" : undefined}
            tabIndex={onDiscordClick ? 0 : undefined}
            onClick={onDiscordClick ? handleClick : undefined}
            onKeyDown={onDiscordClick ? handleKeyDown : undefined}
            $clickable={!!onDiscordClick}
        >
            <PRIcon src="/images/Discord-Logo.svg" alt="Discord" />
            <PRTextWrapper>
                <PRMeta>
                    {onDiscordClick ? (
                        <DiscordLinkText>{messageText}</DiscordLinkText>
                    ) : (
                        messageText
                    )}
                </PRMeta>
            </PRTextWrapper>
        </DiscordSummaryRow>
    );
};

const DetailWrap = styled.div`
    margin-top: 5px;
    margin-bottom: 5px;
`;

const EventDetailItem = styled.div`
    display: flex;
    align-items: center;
    margin-right: 14px;
    margin-left: 8px;
    text-align: left;
    gap: 4px;
    margin-top: 2px;
    line-height: 1.2;
    color: #969a97;
    font: 500 14px Roboto, sans-serif;
`;

const Icon = styled.img`
    aspect-ratio: 1;
    object-fit: contain;
    object-position: center;
    width: 24px;
`;

const CalendarIcon = styled(Icon)`
    width: 20px;
    height: 20px;
    margin-right: 4px;
    object-fit: contain;
    flex-shrink: 0;
    order: 1px solid #fff;   
    box-sizing: content-box;    
`;

const TimeWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 400;
`;

const EndTime = styled.span`
    color: ${(props) => (props.$isCrossDate ? "#c51212" : "inherit")};
`;

const ExpandFormat = styled.div`
    cursor: pointer;
    padding-left: 2px;
    text-align: center;
    margin-bottom: -5px;
    color: #5893e1;
`;

const PRDetailItem = styled(EventDetailItem)`
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  line-height: 1.2;
`;

const DiscordSummaryRow = styled(PRDetailItem)`
  ${(p) => p.$clickable && `
    cursor: pointer;
    border: none;
    background: transparent;
    text-align: left;
    padding: 0;
  `}
`;

const DiscordLinkText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1a73e8;
  text-decoration: underline;
  &:hover {
    color: #0c52d0;
  }
`;

const PRIcon = styled.img`
  width: ${p => p.$size || '20px'};
  height: ${p => p.$size || '20px'};
  object-fit: contain;
  flex-shrink: 0;
  margin-right: 2px;
  border: 1px solid #fff;    
  box-sizing: content-box;    
`;

const PRTextWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;  
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;

    /* WebKit (Chrome/Edge/Safari) */
    &::-webkit-scrollbar {
    height: 1px;     
    }
    &:hover::-webkit-scrollbar { height: 2px; }
    /* Firefox */
    scrollbar-width: thin; 
`;

const PRMeta = styled.div`
  font-size: 14px;
  color: #444;
  font-weight: 400;
  display: inline-flex;  
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
`;

const PRLink = styled.a`
  font-size: 14px;
  font-weight: 500;
  color: #1a73e8;
  text-decoration: underline;

  &:hover {
    color: #0c52d0;
  }
`;

const AdditionText = styled.span`
  color: #28a745; /* GitHub green */
  font-weight: 500;
`;

const DeletionText = styled.span`
  color: #dc3545; /* GitHub red */
  font-weight: 500;
`;

const CalendarDetailWrapper = styled.div`
  margin-bottom: 8px;
`;

const MissingMeta = styled.div`
  color: #c1121f;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export default EventDetail;
